<script setup lang="ts">
import {
  ref,
  computed,
  onMounted,
  watch,
  nextTick,
  onBeforeUnmount,
} from "vue";
import {
  CashierDuty,
  PaymentReceipts,
  PaymentType,
  PosStation,
  SalesDocument,
  SalesRep,
} from "../../../../../types/models";
import AsyncImage from "../../../../../components/ui/form/AsyncImage.vue";
import IconComponent from "../../../../../components/ui/common/IconComponent.vue";
import { User } from "../../../../../types/auth";
import { posStore } from "../../../../../stores/posStore";
import TextInput from "../../../../../components/ui/form/TextInput.vue";
import CheckInput from "../../../../../components/ui/form/CheckInput.vue";
import SearchSelect from "../../../../../components/ui/form/SearchSelect.vue";
import FileUpload from "../../../../../components/ui/form/FileUpload.vue";
import {
  isEmpty,
  printPDFBlob,
  sharePDFBlob,
} from "../../../../../utils/CommonFunctions";
import Buttons from "../../../../../components/ui/form/Buttons.vue";
import MessageBox from "../../../../../services/MessageBox";
import { api } from "../../../../../api/Api";
import Toast from "../../../../../services/Toast";

const props = defineProps<{
  payment_types: PaymentType[];
  delivery_staffs: User[];
  posStation: PosStation | undefined;
  cashierDuty: CashierDuty | null;
  salesReps: SalesRep[];
}>();

const emit = defineEmits<{
  (e: "close"): void;
  (e: "new", data: any, tables: any[]): void;
  (e: "complete", paymentData: any): void;
  (e: "getDeliverys"): void;
}>();

const pos = posStore();
const payments = ref<PaymentReceipts[]>([]);
const currentAmount = ref("");
const currentReference = ref("");
const currentAttachment = ref("");
const selectedType = ref<PaymentType>(
  props.payment_types && props.payment_types.length > 0
    ? props.payment_types[0]
    : ({} as PaymentType)
);
const amountInput = ref<HTMLInputElement | null>(null);
const addPaymentButton = ref<HTMLButtonElement | null>(null);

const totalPaid = computed(() => {
  return roundToDecimalPlace(
    payments.value.reduce((sum, payment) => sum + payment.amount, 0)
  );
});

function roundToDecimalPlace(number: number, decimalPlaces = 2) {
  const factor = Math.pow(10, decimalPlaces);
  return Math.round(number * factor) / factor;
}

const remainingAmount = computed(() => {
  return roundToDecimalPlace((pos.net_total ?? 0) - totalPaid.value);
});

const change = computed(() => {
  return roundToDecimalPlace(totalPaid.value - (pos.net_total ?? 0));
});

const addPayment = () => {
  const amount = parseFloat(currentAmount.value);
  if (!amount || amount <= 0) return;

  console.log(
    "amount",
    amount,
    "runnuing",
    remainingAmount.value,
    "paid",
    totalPaid.value
  );
  console.log("pos net total", pos.net_total);
  // For non-cash payments, don't allow exceeding the total
  if (!selectedType.value.allow_over_tender && amount > remainingAmount.value) {
    return;
  }

  if (selectedType.value.require_reference && !currentReference.value) {
    MessageBox.show("Reference is required", "warning");
    return;
  }

  if (selectedType.value.require_attachment && !currentAttachment.value) {
    MessageBox.show("Attachment is required", "warning");
    return;
  }

  console.log("change", change.value);
  let pay: PaymentReceipts = {
    id: "",
    date_time: new Date().toISOString().split("T")[0],
    prefix: "",
    suffix: "",
    ref_no: "",
    payment_types_id: selectedType.value.id,
    payment_type: selectedType.value,
    currencys_id: props.posStation?.outlet?.currencys_id ?? 1,
    currency: props.posStation?.outlet?.currency ?? "",
    exchange_rate: props.posStation?.outlet?.exchange_rate ?? 1,
    amount: amount,
    tendered: amount,
    change: change.value,
    outlets_id: props.posStation?.outlet?.id ?? 1,
    outlet_pos_stations_id: props.posStation?.id ?? 1,
    cashier_dutys_id: 1, //TODO: Get Cashier Duty
    customers_id: "",
    shift_end_reports_id: "",
    attachment: currentAttachment.value,
    reference: currentReference.value,
    status_id: "",
    users_id: "",
    uid: "",
  };

  nextTick(() => {
    console.log(selectedType.value)
    //if (selectedType.value.allow_over_tender == true) {
    const rem = (pos.net_total ?? 0) - totalPaid.value;
      console.log('amount', amount, 'remaining', remainingAmount.value, 'rem', rem);
      if (remainingAmount.value < 0) {
        console.log('exceeding amount');
        pay.amount = amount + rem;
      } else {
        pay.amount = parseFloat(amount.toFixed(2));
      }
      /*pay.amount = parseFloat(
        (
          parseFloat(pay.amount?.toString() ?? "0") -
          parseFloat(change.value?.toString() ?? "0")
        ).toFixed(2)
      );*/
      pay.change = change.value;
   // }
  });

  console.log('pay', pay)
  payments.value.push(pay);
  currentAmount.value = "";
  currentReference.value = "";
  currentAttachment.value = "";
  selectedType.value = props.payment_types[0];

  console.log(payments.value);

  // Focus amount input after adding payment
  focusAmountInput();
};

const sales_reps_id = ref<string | number | null>(null);
const to_delviery = ref<boolean>(false);
const customer_reference = ref<string>("");

const removePayment = (index: number) => {
  payments.value.splice(index, 1);
};

const endPoint = "revenue/sales/pos/";
const handleUpdate = async (
  print = false,
  share = false,
  delivery = false,
  delivery_staff: string | number = "",
  thermal = true
) => {
  if (totalPaid.value < (pos.net_total ?? 0)) return;

  if (props.cashierDuty == null) {
    MessageBox.show("Cashier Duty is required", "warning");
    return;
  }

  let invoice: SalesDocument =
    pos.selectedPosInvoice?.invoice ?? ({} as SalesDocument);

  invoice.to_deliver = delivery;
  if (!delivery) {
    invoice.to_deliver = delivery_staff ? true : to_delviery.value ?? false;
  }

  invoice.delivery_staffs_id = delivery_staff;

  invoice.warehouses_id = props.posStation?.outlet?.warehouses_id ?? "";
  invoice.outlets_id = props.posStation?.outlet?.id ?? "";
  invoice.outlet_pos_stations_id = props.posStation?.id ?? "";
  invoice.price_lists_id = props.posStation?.outlet?.price_lists_id ?? "";
  invoice.sales_channels_id = props.posStation?.outlet?.sales_channels_id ?? "";
  invoice.sales_reps_id = sales_reps_id.value ?? "";

  //console.log("delivery", delivery);
  //invoice.to_deliver = delivery;
  invoice.thermal = thermal;
  invoice.share = share || print;
  invoice.delivery = delivery;
  if (
    !invoice.delivery_address_id &&
    invoice.customer?.addresses &&
    invoice.customer.addresses.length > 0
  ) {
    invoice.delivery_address_id = invoice.customer?.addresses?.[0]?.id ?? "";
  }

  if (invoice.customer) {
    invoice.credit_terms_id = invoice.customer?.credit_terms_id ?? 1;
    // Set due_date to 10 days from now
    const dueDate = new Date();
    dueDate.setDate(dueDate.getDate() + (invoice.customer?.credit_days ?? 0));
    invoice.due_date = dueDate.toISOString().split("T")[0];
  }

  invoice.payments = payments.value;
  invoice.cashier_dutys_id = props.cashierDuty ? props.cashierDuty.id : "";

  invoice.left_margin = props.posStation?.left_margin ?? 0;
  invoice.right_margin = props.posStation?.right_margin ?? 0;
  invoice.top_margin = props.posStation?.top_margin ?? 0;
  invoice.button_margin = props.posStation?.button_margin ?? 0;
  invoice.font_size = props.posStation?.font_size ?? 0;
  invoice.font_bold = props.posStation?.font_bold ?? false;
  invoice.recepit_size = props.posStation?.recepit_size ?? 80;
  invoice.print_barcode = props.posStation?.print_barcode ?? true;
  invoice.customer_reference = customer_reference.value;

  const response = await api.post(endPoint + "invoice", invoice);
  if (response.success == true) {
    Toast.show("Invoice Created", "info");
    emit("close");
    emit("new", response.data.data, response.data.tables);
    if (print) {
      printPDFBlob(response.data.file);
    }
    if (share) {
      sharePDFBlob(response.lists, "Bill #:" + response.data.data.document_no);
    }
    if (delivery) {
      emit("getDeliverys");
    }
    //printInvoice(response.data, props.posStation);
    pos.voidInvoice();
  } else {
    Toast.show("Could not update Invoice", "warning");
  }

  emit("complete", {
    payments: payments.value,
    totalPaid: totalPaid.value,
    change: change.value,
  });
};

const currencyCode = computed(() => {
  return props.posStation?.outlet?.currency_code ?? "";
});
const formatCurrency = (amount: number) => {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: currencyCode.value,
  }).format(amount);
};

const focusAmountInput = () => {
  // Use nextTick to ensure DOM is updated
  nextTick(() => {
    amountInput.value?.focus();
  });
};

const scrollContainer = ref<HTMLElement | null>(null);
const isDragging = ref(false);
const startX = ref(0);
const scrollLeft = ref(0);

const startDrag = (e: { pageX: number }) => {
  isDragging.value = true;
  if (scrollContainer.value) {
    startX.value = e.pageX - scrollContainer.value.offsetLeft;
    scrollLeft.value = scrollContainer.value.scrollLeft;
  }
  document.addEventListener("mousemove", onDrag);
  document.addEventListener("mouseup", stopDrag);
};

const onDrag = (e: { preventDefault: () => void; pageX: number }) => {
  if (!isDragging.value) return;
  e.preventDefault();
  if (!scrollContainer.value) return;
  const x = e.pageX - scrollContainer.value.offsetLeft;
  const walk = (x - startX.value) * 3; //scroll-fast
  scrollContainer.value.scrollLeft = scrollLeft.value - walk;
};

const stopDrag = () => {
  isDragging.value = false;
  document.removeEventListener("mousemove", onDrag);
  document.removeEventListener("mouseup", stopDrag);
};

const handleSelectType = (type: PaymentType) => {
  console.log("Selected payment type:", type);
  selectedType.value = type;
  currentAmount.value = "";
  currentReference.value = "";
  currentAttachment.value = "";
  focusAmountInput();
};

const handleKeyPress = (event: KeyboardEvent) => {
  if (event.key === "F12") {
    event.preventDefault();
    handleUpdate(true);
  } else if (event.key === "F9") {
    event.preventDefault();
    handleUpdate(false, true);
  } else if (event.key === "F10") {
    event.preventDefault();
    if (
      totalPaid.value >= (pos.net_total ?? 0) &&
      !isEmpty(pos.selectedPosInvoice?.invoice.customers_id) &&
      pos.selectedPosInvoice?.invoice?.customer?.selectedAddress
    ) {
      handleUpdate(false, false, true);
    }
  } else if (event.key === "F11") {
    event.preventDefault();
    handleUpdate();
  } else if (event.key === "F8") {
    event.preventDefault();
    handleCancel();
  } else if (
    event.ctrlKey &&
    event.code != "ControlLeft" &&
    event.code != "ControlRight"
  ) {
    event.preventDefault();
    let key = `Ctrl+${event.key}`.toLowerCase();
    let paymentType = props.payment_types.find(
      (t) => t.shortcut?.toLowerCase() === key
    );
    if (paymentType) {
      handleSelectType(paymentType);
    }
  }
};

// Focus amount input when component is mounted
onMounted(() => {
  console.log(pos.selectedPosInvoice?.invoice);
  window.window.addEventListener("keydown", handleKeyPress);
  if (scrollContainer.value) {
    scrollContainer.value.addEventListener("mousedown", startDrag);
  }
  focusAmountInput();
});

onBeforeUnmount(() => {
  window.removeEventListener("keydown", handleKeyPress);
  if (scrollContainer.value) {
    scrollContainer.value.removeEventListener("mousedown", startDrag);
  }
});

// Watch for payment type changes to focus amount input
watch(selectedType, () => {
  focusAmountInput();
});

const handleCancel = () => {
  emit("close");
};

const hanldeOcrRead = (reference: string, amount: string, file: string) => {
  if (reference) currentReference.value = reference;
  if (amount) {
    currentAmount.value = amount;
  }
  if (file) currentAttachment.value = file;
  if (
    currentAmount.value &&
    currentReference.value &&
    currentAttachment.value
  ) {
    addPayment();
  }
};
</script>

<template>
  <div
    class="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[100] p-4"
  >
    <div
      class="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-3xl w-full"
    >
      <!-- Header with Total -->
      <div class="p-6 border-b border-gray-200 dark:border-gray-700">
        <div class="flex justify-between items-center">
          <h3 class="text-xl font-semibold text-gray-900 dark:text-white">
            Checkout
          </h3>
          <div class="text-2xl font-bold text-gray-900 dark:text-white">
            Invoice Total: {{ formatCurrency(pos.net_total ?? 0) }}
          </div>
        </div>
      </div>
      <!-- Content -->
      <div class="p-6">
        <div
          class="mt-1 grid grid-cols-5 gap-4 mb-6 bg-gray-500 dark:bg-gray-200 rounded-lg p-4"
        >
          <div class="flex items-center col-span-2">
            <!--label class="block text-sm font-medium text-gray-300 dark:text-gray-700 mb-1">Sales Rep</label>-->
            <SearchSelect
              v-model="sales_reps_id"
              label=" "
              placeholder="Select Sales Rep"
              :options="salesReps"
            />
          </div>
          <div class="flex items-center gap-2">
            <CheckInput
              v-model="to_delviery"
              label=""
              class="col-span-1"
              :readonly="
                isEmpty(pos.selectedPosInvoice?.invoice.customers_id) ||
                (pos.selectedPosInvoice?.invoice?.customer?.addresses?.length ??
                  0) <= 0
              "
            />
            <label
              class="block text-sm font-medium text-gray-300 dark:text-gray-700 mb-1"
              >Track Delivery</label
            >
          </div>
          <div class="flex items-center col-span-2">
            <!--<label class="block text-sm font-medium text-gray-300 dark:text-gray-700 mb-1">Customer Reference</label>-->
            <TextInput
              v-model="customer_reference"
              label=" "
              placeholder="Customer Reference"
              class="w-full"
            />
          </div>
        </div>
        <!-- Payment Type Selection -->
        <div
          class="flex gap-2 mb-6 overflow-x-auto"
          ref="scrollContainer"
          @mousedown="startDrag"
        >
          <button
            v-for="type in payment_types"
            :key="type.id"
            @click="handleSelectType(type)"
            class="flex-1 p-4 rounded-lg border-2 transition-colors flex flex-col items-center gap-2 min-w-[150px]"
            :class="[
              selectedType.id === type.id
                ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/50'
                : 'border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800',
              payments.some((p) => p.payment_type?.id === type.id)
                ? 'opacity-50 cursor-not-allowed'
                : '',
            ]"
            :disabled="
              payments.some((p) => p.payment_type?.id === type.id) ||
              (type.require_customer &&
                (pos.selectedPosInvoice?.invoice.customers_id == '' ||
                  (type.check_credit &&
                    isEmpty(
                      pos.selectedPosInvoice?.invoice.customer?.credit_terms_id
                    )) ||
                  (type.check_credit &&
                    Number(
                      pos.selectedPosInvoice?.invoice.customer?.outstanding ?? 0
                    ) >
                      Number(
                        pos.selectedPosInvoice?.invoice.customer
                          ?.credit_limit ?? 0
                      ))))
            "
            :style="{
              cursor:
                type.require_customer &&
                (pos.selectedPosInvoice?.invoice.customers_id == '' ||
                  (type.check_credit &&
                    isEmpty(
                      pos.selectedPosInvoice?.invoice.customer?.credit_terms_id
                    )) ||
                  (type.check_credit &&
                    Number(
                      pos.selectedPosInvoice?.invoice.customer?.outstanding ?? 0
                    ) >
                      Number(
                        pos.selectedPosInvoice?.invoice.customer
                          ?.credit_limit ?? 0
                      )))
                  ? 'not-allowed'
                  : 'pointer',
              backgroundColor:
                type.require_customer &&
                (pos.selectedPosInvoice?.invoice.customers_id == '' ||
                  (type.check_credit &&
                    isEmpty(
                      pos.selectedPosInvoice?.invoice.customer?.credit_terms_id
                    )) ||
                  (type.check_credit &&
                    Number(
                      pos.selectedPosInvoice?.invoice.customer?.outstanding ?? 0
                    ) >
                      Number(
                        pos.selectedPosInvoice?.invoice.customer
                          ?.credit_limit ?? 0
                      )))
                  ? '#464646'
                  : 'inherit',
            }"
          >
            <AsyncImage
              class-name="h-6"
              :src="type.icon_image"
              :alt="type.name"
            />
            <span class="text-sm font-medium">{{ type.name }}</span>
            <template
              v-if="
                type.require_customer &&
                (pos.selectedPosInvoice?.invoice.customers_id == '' ||
                  (type.check_credit &&
                    isEmpty(
                      pos.selectedPosInvoice?.invoice.customer?.credit_terms_id
                    )) ||
                  (type.check_credit &&
                    Number(
                      pos.selectedPosInvoice?.invoice.customer?.outstanding ?? 0
                    ) >
                      Number(
                        pos.selectedPosInvoice?.invoice.customer
                          ?.credit_limit ?? 0
                      )))
              "
            >
              <span
                v-if="
                  type.check_credit &&
                  pos.selectedPosInvoice?.invoice.customers_id &&
                  isEmpty(
                    pos.selectedPosInvoice?.invoice.customer?.credit_terms_id
                  )
                "
                class="text-sm font-medium"
                >Credit Not Allowed</span
              >
              <span
                v-else-if="
                  type.check_credit &&
                  Number(
                    pos.selectedPosInvoice?.invoice.customer?.outstanding ?? 0
                  ) >
                    Number(
                      pos.selectedPosInvoice?.invoice.customer?.credit_limit ??
                        0
                    )
                "
                class="text-sm font-medium"
                >Credit Limit Exceeded</span
              >
              <span v-else class="text-sm font-medium">Customer Required</span>
            </template>
            <span v-else-if="type.shortcut" class="text-sm font-medium"
              >({{ type.shortcut }})</span
            >
          </button>
        </div>

        <!-- Amount Input -->
        <div class="mb-6">
          <div class="flex gap-2">
            <div class="flex-1">
              <input
                ref="amountInput"
                v-model="currentAmount"
                type="number"
                step="0.01"
                min="0"
                :max="
                  selectedType.allow_over_tender ? undefined : remainingAmount
                "
                placeholder="Enter amount"
                class="w-[100%] px-4 py-2 text-right text-2xl font-bold text-gray-900 dark:text-white border border-gray-200 dark:border-gray-700 rounded-lg"
                @keyup.enter="addPayment"
              />
              <FileUpload
                label=""
                placeholder="Attachment"
                path="payment_receipts"
                ref="attachment"
                v-model="currentAttachment"
                v-if="selectedType.require_attachment"
                :ocr="selectedType.read_ocr"
                :ocr-length="selectedType.ocr_string_length"
                :ocr-match="selectedType.ocr_init_string"
                :currency="posStation?.outlet?.currency_code"
                @ocrRead="hanldeOcrRead"
              />
              <TextInput
                label=""
                placeholder="Reference"
                ref="reference"
                v-model="currentReference"
                v-if="selectedType.require_reference"
              />
            </div>
            <button
              @click="addPayment"
              ref="addPaymentButton"
              caption="Add Payment"
              className="py-2 px-4 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              :disabled="
                !currentAmount ||
                parseFloat(currentAmount) <= 0 ||
                (selectedType.require_reference && !currentReference) ||
                (selectedType.require_attachment && !currentAttachment) ||
                (selectedType.require_customer &&
                  pos.selectedPosInvoice?.invoice.customers_id == '') ||
                (selectedType.check_credit &&
                  Number(
                    pos.selectedPosInvoice?.invoice.customer?.credit_limit ?? 0
                  ) < Number(currentAmount ?? 0))
              "
            >
              Add Payment
            </button>
          </div>
          <span v-if="parseFloat(currentAmount) <= 0"
            >Enter Amount for Payment</span
          >
          <span v-else-if="selectedType.require_reference && !currentReference"
            >Enter The reference to proceed</span
          >
          <span
            v-else-if="selectedType.require_attachment && !currentAttachment"
            >Select the Attachment to proceed</span
          >
          <span
            v-else-if="
              selectedType.require_customer &&
              pos.selectedPosInvoice?.invoice.customers_id == ''
            "
            >Select a Customer to Proceed</span
          >
          <span
            v-else-if="
              selectedType.check_credit &&
              Number(
                pos.selectedPosInvoice?.invoice.customer?.credit_limit ?? 0
              ) < Number(currentAmount ?? 0)
            "
            >Customer do not have enough Credit Limit</span
          >
        </div>

        <!-- Payment Details Grid -->
        <div class="bg-gray-50 dark:bg-gray-900 rounded-lg p-4 mb-6">
          <div class="space-y-2">
            <div
              v-for="(payment, index) in payments"
              :key="index"
              class="grid grid-cols-4 gap-4 items-center py-2 border-b border-gray-200 dark:border-gray-700"
            >
              <div class="flex items-center gap-2">
                <span class="text-xl w-[80px]"
                  ><AsyncImage
                    class-name="max-h-[30px]"
                    :src="
                      payment_types.find(
                        (t) => t.id === payment.payment_type?.id
                      )?.icon_image
                    "
                /></span>
                <span>{{
                  payment_types.find((t) => t.id === payment.payment_type?.id)
                    ?.name
                }}</span>
              </div>
              <div class="text-right font-medium text-gray-900 dark:text-white">
                {{ formatCurrency(payment.tendered) }}
              </div>
              <div class="text-right font-medium text-gray-900 dark:text-white">
                {{
                  formatCurrency(
                    payments
                      .slice(0, index + 1)
                      .reduce((sum, p) => sum + p.tendered, 0)
                  )
                }}
              </div>
              <div class="text-right">
                <button
                  @click="removePayment(index)"
                  class="text-red-500 hover:text-red-600"
                >
                  <IconComponent name="Trash2" />
                </button>
              </div>
            </div>
          </div>

          <!-- Totals -->
          <div class="mt-4 space-y-2">
            <div class="flex justify-between text-sm">
              <span class="font-medium text-gray-700 dark:text-gray-300"
                >Total Paid</span
              >
              <span class="font-bold text-gray-900 dark:text-white">{{
                formatCurrency(totalPaid)
              }}</span>
            </div>
            <div class="flex justify-between text-sm">
              <span class="font-medium text-gray-700 dark:text-gray-300"
                >Remaining</span
              >
              <span class="font-bold text-blue-600">{{
                formatCurrency(
                  totalPaid < (pos.net_total ?? 0)
                    ? (pos.net_total ?? 0) - totalPaid
                    : 0
                )
              }}</span>
            </div>
            <div v-if="change > 0" class="flex justify-between text-sm">
              <span class="font-medium text-gray-700 dark:text-gray-300"
                >Change</span
              >
              <span class="font-bold text-green-600">{{
                formatCurrency(change)
              }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Footer -->
      <div
        class="px-6 py-4 bg-gray-50 dark:bg-gray-700/50 rounded-b-lg flex justify-between"
      >
        <div class="flex gap-3">
          <Buttons
            caption=""
            icon="Share2"
            @click="handleUpdate(false, true, false)"
            :disabled="totalPaid < (pos.net_total ?? 0)"
            className="px-2 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center gap-2"
          />
          <Buttons
            caption="Delivery"
            icon="Truck"
            @click="handleUpdate(false, false, true)"
            :disabled="
              totalPaid < (pos.net_total ?? 0) ||
              isEmpty(pos.selectedPosInvoice?.invoice.customers_id) ||
              (pos.selectedPosInvoice?.invoice?.customer?.addresses?.length ??
                0) <= 0
            "
            className="px-2 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center gap-2"
          />
          <Buttons
            caption="Update (F11)"
            icon="Save"
            @click="handleUpdate()"
            :disabled="totalPaid < (pos.net_total ?? 0)"
            className="px-2 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center gap-2"
          />
          <Buttons
            caption="Print (F12)"
            icon="ReceiptText"
            @click="handleUpdate(true)"
            :disabled="totalPaid < (pos.net_total ?? 0)"
            className="px-2 py-4 text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center gap-2"
          />
          <Buttons
            caption="Print (A4)"
            icon="Printer"
            @click="handleUpdate(true, false, false, '', false)"
            :disabled="totalPaid < (pos.net_total ?? 0)"
            className="px-2 py-4 text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center gap-2"
          />
        </div>
        <div class="flex gap-3">
          <Buttons
            caption="Cancel (F8)"
            icon="LogOut"
            @click="handleCancel"
            className="px-2 py-4 text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-red-800 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center gap-2"
          />
        </div>
      </div>
    </div>
    <div ref="printArea" id="printArea" style="display: none"></div>
  </div>
</template>
